import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Dialog/menu focus management (the §11 standard): while `active`, keep focus
 * inside `containerRef` (Tab/Shift-Tab wrap), close on Esc, and return focus to
 * the element that had it when the trap engaged. On engage, focus moves to the
 * first focusable inside the container UNLESS focus is already inside (so a
 * component that focuses its own field first — e.g. an autofocused input — is
 * respected). `onClose` is read through a ref, so passing a fresh inline arrow
 * every render does not rebuild the trap.
 */
export function useFocusTrap(
  active: boolean,
  containerRef: RefObject<HTMLElement | null>,
  onClose?: () => void,
) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!active) return;
    const invoker = document.activeElement as HTMLElement | null;

    const raf = requestAnimationFrame(() => {
      const el = containerRef.current;
      if (el && !el.contains(document.activeElement)) {
        el.querySelector<HTMLElement>(FOCUSABLE)?.focus();
      }
    });

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key !== 'Tab' || !containerRef.current) return;
      const f = Array.from(containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (f.length === 0) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
      // Return focus to the invoker ONLY if focus wasn't deliberately moved
      // elsewhere. By cleanup time the container has unmounted, so a focused
      // descendant has already collapsed to <body> (Esc / backdrop dismissal
      // ⇒ restore). But if some OTHER element now holds focus, the user moved
      // it on purpose — e.g. clicked into the composer to dismiss a non-modal
      // popover — and yanking the caret back to the trigger would be wrong.
      const ae = document.activeElement;
      if (ae === null || ae === document.body) invoker?.focus?.();
    };
  }, [active, containerRef]);
}
